// Live's project tree (spec A3b, design-ref/folders-410/02): the brand's folders as rows, each
// level one step in, a chevron on the right of a folder that has folders under it. The library's
// panel, the pickers and the breadcrumbs' path all draw it with this one component.
import { ActionIcon, Loader, TextInput, UnstyledButton } from "@mantine/core";
import { ChevronDown, ChevronRight, Folder as FolderIcon } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import type { Folder } from "../../api/generated/model";
import { folderInNameMax } from "../../api/generated/zod";
import { folderPath, type FolderTree } from "./folder-tree";
import classes from "./folders.module.css";

export interface FolderTreeListProps {
  tree: FolderTree;
  /** The folder shown as chosen; its parents start open. */
  selectedId: string | null | undefined;
  onSelect: (folder: Folder) => void;
  /** Folders that cannot be chosen (a move's own folder and those under it). */
  disabledIds?: ReadonlySet<string> | undefined;
  /** A search's hits and the folders above them; every one of them shows open. */
  shownIds?: ReadonlySet<string> | undefined;
  /** What shows at the row's start on hover, in the folder icon's place (the library's star). */
  rowStart?: ((folder: Folder) => ReactNode) | undefined;
  /** What shows at the row's end on hover (the library's menu). */
  rowEnd?: ((folder: Folder) => ReactNode) | undefined;
  /** The row whose end stays shown, as while its menu is open. */
  pinnedId?: string | undefined;
  /** A name typed in the tree, as live's: a folder renamed in place, or a new folder under one
   *  (its "Add subproject"). Enter or leaving the field submits; Escape or an empty name cancels. */
  editing?: FolderEdit | undefined;
  onEdit?: ((name: string) => void) | undefined;
  onEditCancel?: (() => void) | undefined;
  editPending?: boolean | undefined;
  label: string;
}

export type FolderEdit = { kind: "rename"; folderId: string } | { kind: "child"; parentId: string };

/** Live's in-tree name field (`CreateProjectInput`): it submits on Enter or when it loses focus,
 *  and an empty name, or Escape, cancels. */
export function FolderNameField(props: {
  initial: string;
  placeholder: string;
  label: string;
  pending?: boolean | undefined;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(props.initial);
  const [done, setDone] = useState(false);
  // The field opens where the user asked for it, so it takes the focus, as live's does.
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);
  // One submit per name typed: the disabled field's blur while it saves sends nothing more. A
  // refused name keeps the field; typing allows another try, and Escape still closes it.
  const finish = (submit: boolean) => {
    const name = value.trim();
    if (submit && name !== "" && name !== props.initial) {
      if (done) return;
      setDone(true);
      props.onSubmit(name);
      return;
    }
    setDone(true);
    props.onCancel();
  };
  return (
    <TextInput
      ref={input}
      size="xs"
      aria-label={props.label}
      placeholder={props.placeholder}
      value={value}
      maxLength={folderInNameMax}
      disabled={props.pending === true}
      leftSection={
        props.pending === true ? <Loader size={14} /> : <FolderIcon size={16} aria-hidden />
      }
      onChange={(event) => {
        setValue(event.currentTarget.value);
        setDone(false);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") finish(true);
        if (event.key === "Escape") finish(false);
      }}
      onBlur={() => finish(true)}
    />
  );
}

export function FolderTreeList(props: FolderTreeListProps) {
  const { tree, selectedId, onSelect, disabledIds, shownIds, rowStart, rowEnd, pinnedId } = props;
  const { editing, onEdit, onEditCancel, editPending } = props;
  const [open, setOpen] = useState<ReadonlySet<string>>(
    () => new Set(folderPath(tree, selectedId ?? undefined).slice(0, -1).map((f) => f.id)),
  );
  const toggle = (id: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rows = (parentId: string | null, depth: number): ReactNode[] =>
    tree
      .children(parentId)
      .filter((folder) => shownIds === undefined || shownIds.has(folder.id))
      .flatMap((folder) => {
        const addingHere = editing?.kind === "child" && editing.parentId === folder.id;
        const hasChildren = tree.children(folder.id).length > 0;
        const expanded =
          addingHere || (hasChildren && (shownIds !== undefined || open.has(folder.id)));
        const renaming = editing?.kind === "rename" && editing.folderId === folder.id;
        const disabled = disabledIds?.has(folder.id) === true;
        const selected = folder.id === selectedId;
        const start = rowStart?.(folder);
        const row = (
          <div
            key={folder.id}
            role="treeitem"
            aria-level={depth + 1}
            aria-selected={selected}
            aria-expanded={hasChildren ? expanded : undefined}
            aria-disabled={disabled || undefined}
            className={classes.row}
            data-selected={selected || undefined}
            data-disabled={disabled || undefined}
            data-has-start={start === undefined ? undefined : true}
            style={{ "--folder-depth": depth } as React.CSSProperties}
          >
            {start === undefined || renaming ? null : (
              <span className={classes.start}>{start}</span>
            )}
            {renaming ? (
              <div className={classes.field}>
                <FolderNameField
                  initial={folder.name}
                  placeholder="Project name"
                  label={`Rename ${folder.name}`}
                  pending={editPending}
                  onSubmit={(name) => onEdit?.(name)}
                  onCancel={() => onEditCancel?.()}
                />
              </div>
            ) : (
              <UnstyledButton
                className={classes.choose}
                disabled={disabled}
                onClick={() => onSelect(folder)}
                aria-current={selected ? "true" : undefined}
              >
                <FolderIcon size={16} strokeWidth={1.5} className={classes.icon} aria-hidden />
                <span className={classes.name}>{folder.name}</span>
              </UnstyledButton>
            )}
            {rowEnd === undefined ? null : (
              <span className={classes.end} data-open={pinnedId === folder.id || undefined}>
                {rowEnd(folder)}
              </span>
            )}
            {hasChildren ? (
              <ActionIcon
                variant="subtle"
                size="sm"
                className={classes.chevron}
                aria-label={`${expanded ? "Collapse" : "Expand"} ${folder.name}`}
                onClick={() => toggle(folder.id)}
              >
                {expanded ? (
                  <ChevronDown size={16} aria-hidden />
                ) : (
                  <ChevronRight size={16} aria-hidden />
                )}
              </ActionIcon>
            ) : null}
          </div>
        );
        const adding = addingHere ? (
          <div
            key={`${folder.id}-new`}
            className={classes.row}
            style={{ "--folder-depth": depth + 1 } as React.CSSProperties}
          >
            <div className={classes.field}>
              <FolderNameField
                initial=""
                placeholder="New project"
                label={`New project in ${folder.name}`}
                pending={editPending}
                onSubmit={(name) => {
                  // The new subproject shows where it was made: its parent stays open.
                  setOpen((current) => new Set(current).add(folder.id));
                  onEdit?.(name);
                }}
                onCancel={() => onEditCancel?.()}
              />
            </div>
          </div>
        ) : null;
        return expanded ? [row, adding, ...rows(folder.id, depth + 1)] : [row];
      });

  return (
    <div role="tree" aria-label={props.label} className={classes.tree}>
      {rows(null, 0)}
    </div>
  );
}
